/**
 * Utility functions for the ZapSEA JavaScript SDK
 */

/**
 * Sleep for a specified number of milliseconds
 */
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Check if we're running in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.document !== 'undefined';
}

/**
 * Check if we're running in a Node.js environment
 */
export function isNode(): boolean {
  return typeof process !== 'undefined' &&
         process.versions !== undefined &&
         typeof process.versions.node === 'string';
}

/**
 * Validate API key format
 */
export function validateApiKey(apiKey: string): boolean {
  if (!apiKey || typeof apiKey !== 'string') {
    return false;
  }
  
  // API keys should start with pk_live_ or pk_test_
  return /^pk_(live|test)_[a-zA-Z0-9]{32,}$/.test(apiKey);
}

/**
 * Sanitize URL by removing trailing slashes
 */
export function sanitizeUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Create a user agent string for the SDK
 */
export function createUserAgent(version: string = '1.0.0'): string {
  const platform = isBrowser() ? 'browser' : 'node';
  return `zapsea-js/${version} (${platform})`;
}

/**
 * Determine if an error should be retried
 */
export function shouldRetryError(error: any): boolean {
  // Retry on network errors
  if (error.name === 'AbortError' || error.name === 'NetworkError') {
    return true;
  }
  
  // Retry on 5xx status codes
  if (error.status >= 500 && error.status < 600) {
    return true;
  }
  
  // Retry on 429 (rate limit) with backoff
  if (error.status === 429) {
    return true;
  }
  
  return false;
}

/**
 * Calculate exponential backoff delay
 */
export function calculateBackoffDelay(attempt: number, baseDelay: number = 1000): number {
  return Math.min(baseDelay * Math.pow(2, attempt), 30000); // Max 30 seconds
}

/**
 * Validate job ID format
 */
export function validateJobId(jobId: string): boolean {
  if (!jobId || typeof jobId !== 'string') {
    return false;
  }
  
  // Job IDs should be UUIDs or similar format
  return /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(jobId) ||
         /^job_[a-zA-Z0-9]{16,}$/.test(jobId);
}

/**
 * Parse error response from API
 */
export async function parseErrorResponse(response: Response): Promise<{ message: string; code?: string }> {
  try {
    const errorData = await response.json();
    return {
      message: errorData.detail || errorData.message || `HTTP ${response.status}`,
      code: errorData.code
    };
  } catch {
    return {
      message: `HTTP ${response.status}: ${response.statusText}`
    };
  }
}
